import { StandardEvents } from '@shopify/events';

/**
 * NIKA: Калкулатор за цена на доставка в количката.
 * Ползва стандартния Shopify endpoint /cart/shipping_rates.json.
 * - валидация на пощенския код (4 цифри) преди заявка
 * - AbortController: нова заявка отменя старата
 * - запомня кода в sessionStorage и преизчислява автоматично при промяна
 *   на количката (StandardEvents.cartLinesUpdate) и след morph re-render
 * - цена 0 → показва само името на метода (без "0,00 €")
 */
const STORAGE_KEY = 'nika-shipest-zip';

const PICK_KEY = 'nika-ship-pick';

/* NIKA: имената идват от настройките за доставка в Shopify. Тук ги
   съкращаваме за показване — "Безплатна доставка с Еконт до адрес"
   ставаше "…: безплатно", което е тавтология. Ключът е част от името. */
const RENAME = [[/безплатна доставка с еконт до адрес/i, '🏠 Еконт доставка до адрес']];

function displayName(name) {
  for (const [re, to] of RENAME) if (re.test(name)) return to;
  return name;
}

class NikaShippingEstimator extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.form = this.querySelector('.nika-shipest__form');
    this.input = this.querySelector('.nika-shipest__input');
    this.button = this.querySelector('.nika-shipest__button');
    this.errorEl = this.querySelector('.nika-shipest__error');
    this.resultsEl = this.querySelector('.nika-shipest__results');
    this.priceFormat = { format: function (n) { var p = Number(n).toFixed(2).split('.'); return p[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0') + ',' + p[1] + '\u00A0€'; } };

    this.form.addEventListener('submit', this.#onSubmit);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

    // След morph/повторно отваряне: върни кода и преизчисли
    const savedZip = sessionStorage.getItem(STORAGE_KEY);
    if (savedZip) {
      this.input.value = savedZip;
      this.#estimate(savedZip, { silent: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#abortController?.abort();
  }

  #onSubmit = (event) => {
    event.preventDefault();
    const zip = this.input.value.trim();
    if (!/^\d{4}$/.test(zip)) {
      this.#showError('Въведете валиден пощенски код (4 цифри).');
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, zip);
    this.#estimate(zip);
  };

  // Количката се промени → преизчисли тихо с последния код
  #onCartUpdate = () => {
    const zip = sessionStorage.getItem(STORAGE_KEY);
    if (zip && !this.resultsEl.classList.contains('hidden')) {
      this.#estimate(zip, { silent: true });
    }
  };

  async #estimate(zip, { silent = false } = {}) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#hideError();
    if (!silent) this.#setLoading(true);

    try {
      const params = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'Bulgaria',
        'shipping_address[province]': '',
      });
      const cartUrl = this.dataset.cartUrl || '/cart';
      const response = await fetch(`${cartUrl}/shipping_rates.json?${params}`, {
        credentials: 'same-origin',
        signal: this.#abortController.signal,
      });
      const data = await response.json();

      if (!response.ok) {
        this.#showError('Не успяхме да изчислим доставка за този пощенски код.');
        return;
      }
      this.#renderRates(data.shipping_rates ?? []);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#showError('Възникна грешка. Опитайте отново.');
    } finally {
      if (!silent) this.#setLoading(false);
    }
  }

  // Отделните Еконт офиси (по един ред на офис) се свиват до един метод "Еконт офис"
  #collapseEcontOffices(rates) {
    const officeRates = rates.filter((rate) => /Еконт офис\s*#/.test(rate.name));
    if (officeRates.length === 0) return rates;

    const cheapest = officeRates.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));
    const collapsed = { ...cheapest, name: '\u{1F4E6} Еконт доставка до офис' };
    const rest = rates.filter((rate) => !officeRates.includes(rate));
    const insertAt = rates.indexOf(officeRates[0]);
    rest.splice(Math.min(insertAt, rest.length), 0, collapsed);
    return rest;
  }

  #renderRates(rates) {
    if (rates.length === 0) {
      this.#showError('Няма налични методи за доставка за този адрес.');
      return;
    }
    this.resultsEl.innerHTML = '';
    const shown = this.#collapseEcontOffices(rates);

    /* NIKA: методите са избираеми — при няколко варианта клиентът сам
       решава кой да влезе в общата сума. Пази се предишният избор. */
    let saved = null;
    try { saved = sessionStorage.getItem(PICK_KEY); } catch (e) { /* ignore */ }
    let selected = shown.find((r) => r.name === saved) || null;
    if (!selected) {
      selected = shown.reduce((a, b) => (a === null || parseFloat(b.price) < parseFloat(a.price) ? b : a), null);
    }

    for (const rate of shown) {
      const price = parseFloat(rate.price);
      const li = document.createElement('li');
      li.className = 'nika-shipest__rate';

      const pick = document.createElement('label');
      pick.className = 'nika-shipest__pick';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'nika-ship-rate';
      input.className = 'nika-shipest__radio';
      input.value = rate.name;
      input.checked = rate === selected;
      input.addEventListener('change', () => {
        try { sessionStorage.setItem(PICK_KEY, rate.name); } catch (e) { /* ignore */ }
        this.#publishOne(rate);
      });

      const label = displayName(rate.name);
      const text = document.createElement('span');
      /* ако името вече казва "безплатна", не долепяме втори път "безплатно" */
      const saysFree = /безплат/i.test(label);
      text.textContent = price > 0
        ? `${label}: ${this.priceFormat.format(price)}`
        : saysFree ? label : `${label}: безплатно`;

      pick.appendChild(input);
      pick.appendChild(text);
      li.appendChild(pick);
      this.resultsEl.appendChild(li);
    }
    this.resultsEl.classList.remove('hidden');
    if (selected) this.#publishOne(selected);
  }

  /* NIKA: избраният метод отива на реда "Доставка" в общата сума */
  #publishOne(rate) {
    const price = parseFloat(rate.price);
    document.dispatchEvent(
      new CustomEvent('nika:shipping-estimated', {
        detail: { price, name: rate.name, formatted: this.priceFormat.format(price) },
      })
    );
  }


  #setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.textContent = isLoading ? 'Изчисляване…' : 'Изчисли';
  }

  #showError(message) {
    document.dispatchEvent(new CustomEvent('nika:shipping-estimated', { detail: null }));
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
    this.resultsEl.classList.add('hidden');
  }

  #hideError() {
    this.errorEl.classList.add('hidden');
  }
}

if (!customElements.get('nika-shipping-estimator')) {
  customElements.define('nika-shipping-estimator', NikaShippingEstimator);
}
